import { useContext, useRef, useState } from "react";
import { useMatch, useNavigate, useResolvedPath } from "react-router-dom";

import { Context } from "../../context/context";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import MedperLogo from "../../assets/MEDPER INFINITY.svg";
import Avatar from "../avatar/avatar.component";
import { FOOTER_NAV, PRIMARY_NAV } from "../layout/nav-items";
import { ChevronDownIcon, CloseIcon, GridIcon } from "../ui/icons";

/**
 * Desktop sidebar. Also used as the body of the mobile drawer, where
 * `onNavigate` closes the drawer after a jump.
 */
const SideNav = ({ onNavigate, showClose = false }) => {
  const { state } = useContext(Context);
  const { user } = state;

  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  const devicePickerRef = useRef(null);
  useOnClickOutside(devicePickerRef, () => setDevicePickerOpen(false));

  const resolved = useResolvedPath("/device/:deviceId/:page");
  const match = useMatch({ path: resolved.pathname, end: true });
  const navigate = useNavigate();

  const deviceId = match?.params.deviceId;
  const currentPage = match?.params.page;
  const nicknames = user?.nicknames || {};

  const go = (path) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  return (
    <aside className="flex h-full w-64 flex-shrink-0 flex-col bg-ink">
      <div className="flex items-center justify-between px-6 py-6">
        <button onClick={() => go("/")} aria-label="MedpeR home">
          <img src={MedperLogo} alt="MedpeR" className="h-6 w-auto" />
        </button>
        {showClose && (
          <button onClick={onNavigate} aria-label="Close menu" className="text-gray-400 transition hover:text-white">
            <CloseIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {!deviceId && (
        <div className="mb-4 px-4">
          <div className="flex w-full items-center gap-3 rounded-xl bg-white/[0.06] p-3">
            <Avatar src={user?.photoURL} size="md">
              {user?.displayName}
            </Avatar>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-white">{user?.displayName}</span>
              <span className="block truncate text-[11px] text-gray-400">{user?.email}</span>
            </span>
          </div>
        </div>
      )}

      {deviceId && (
        <div className="relative mb-4 px-4" ref={devicePickerRef}>
          <button
            onClick={() => setDevicePickerOpen((v) => !v)}
            aria-expanded={devicePickerOpen}
            className="flex w-full items-center gap-3 rounded-xl bg-white/[0.06] p-3 text-left transition hover:bg-white/10"
          >
            <Avatar src={user?.photoURL} size="md">
              {user?.displayName}
            </Avatar>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-white">{user?.displayName}</span>
              <span className="block truncate text-[11px] text-gray-400">{nicknames[deviceId] || deviceId}</span>
            </span>
            <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
          </button>

          {devicePickerOpen && (
            <div className="absolute left-4 right-4 z-30 mt-1 overflow-hidden rounded-xl border border-hairline bg-white py-1 shadow-pop">
              {Object.entries(nicknames).map(([id, nickname]) => (
                <button
                  key={id}
                  onClick={() => {
                    setDevicePickerOpen(false);
                    go(`/device/${id}/${currentPage || "dashboard"}`);
                  }}
                  className={`block w-full truncate px-4 py-2.5 text-left text-sm transition hover:bg-gray-50 ${
                    id === deviceId ? "font-semibold text-primary-700" : "text-gray-600"
                  }`}
                >
                  {nickname || id}
                </button>
              ))}
              <button
                onClick={() => {
                  setDevicePickerOpen(false);
                  go("/");
                }}
                className="flex w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-left text-sm font-semibold text-primary-600 transition hover:bg-gray-50"
              >
                <GridIcon className="h-4 w-4" />
                See all devices
              </button>
            </div>
          )}
        </div>
      )}

      <nav className="flex-1 space-y-1 overflow-y-auto px-4">
        {deviceId ? (
          PRIMARY_NAV.map(({ page, label, Icon }) => (
            <button
              key={page}
              onClick={() => go(`/device/${deviceId}/${page}`)}
              aria-current={currentPage === page ? "page" : undefined}
              className={`nav-item ${currentPage === page ? "active" : ""}`}
            >
              <Icon className="h-[18px] w-[18px] flex-shrink-0" />
              {label}
            </button>
          ))
        ) : (
          <button onClick={() => go("/")} className="nav-item active">
            <GridIcon className="h-[18px] w-[18px] flex-shrink-0" />
            My Devices
          </button>
        )}
      </nav>

      {deviceId && (
        <div className="border-t border-white/10 px-4 py-4">
          {FOOTER_NAV.map(({ page, label, Icon }) => (
            <button
              key={page}
              onClick={() => go(`/device/${deviceId}/${page}`)}
              aria-current={currentPage === page ? "page" : undefined}
              className={`nav-item ${currentPage === page ? "active" : ""}`}
            >
              <Icon className="h-[18px] w-[18px] flex-shrink-0" />
              {label}
            </button>
          ))}
        </div>
      )}
    </aside>
  );
};

export default SideNav;
